/**
 * BiometricsSection.tsx - Секция «Биометрия» (FaceID) в профиле сотрудника
 * Корпоративный портал Zerde Pharmacy
 *
 * Особенности:
 * - Необязательный раздел: вход по лицу дополняет пароль и в заполненность профиля не входит.
 * - Привязка эталона лица через веб-камеру с помощью face-api.js.
 * - Сброс биометрии по кнопке «Сбросить» или автоматически при изменении ФИО.
 * - Оформлен в строгом едином стиле Zerde (Paper, SectionHeaderDivider, ZERDE_COLORS).
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Collapse
} from '@mui/material';
import {
  CheckCircle,
  DeleteOutline,
  VideocamOutlined,
  VideocamOffOutlined,
  Close,
  Refresh,
  QrCode2,
  PhoneIphone,
  TimerOutlined,
  TerminalOutlined,
  Memory
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { ScanFace } from 'lucide-react';
import axios from 'axios';
import { ZERDE_COLORS } from '../constants';
import SectionHeaderDivider from './SectionHeaderDivider';
import { API_CONFIG } from '../../auth/config/authConfig';
import { notify } from '../../notifications';

/**
 * Своя биометрия: пользователь определяется сервером по сессии, логин в запросах не передается.
 * onStatusChange вызывается только после привязки или сброса - страница профиля перечитывает заполненность.
 */
export interface BiometricsSectionProps {
  onStatusChange?: (hasBiometrics: boolean) => void;
}

const BiometricsSection: React.FC<BiometricsSectionProps> = ({
  onStatusChange
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [hasBiometrics, setHasBiometrics] = useState<boolean>(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [resetting, setResetting] = useState<boolean>(false);
  const [openResetDialog, setOpenResetDialog] = useState<boolean>(false);

  // Состояния камеры и процесса сканирования
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [scanStep, setScanStep] = useState<'center' | 'left' | 'right' | 'saving'>('center');
  const [scanProgress, setScanProgress] = useState<number>(0);
  const [scanMessage, setScanMessage] = useState<string>('Инициализация сессии FaceID...');
  const [scanError, setScanError] = useState<string | null>(null);
  const [availableDevices, setAvailableDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');

  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const scanLoopRef = useRef<number | null>(null);
  const isScanningRef = useRef<boolean>(false);
  const livenessSessionIdRef = useRef<string | null>(null);
  const actionsSequenceRef = useRef<string[]>([]);
  const isProcessingFrameRef = useRef<boolean>(false);
  const hiddenCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Телеметрия нейросети и журнал событий в реальном времени
  const [modelInfo, setModelInfo] = useState<{ detector: string; recognizer: string; landmarks: string; runtime: string } | null>({
    detector: 'SCRFD-10G (InsightFace)',
    recognizer: 'ArcFace ResNet-50 (512-dim)',
    landmarks: 'SCRFD 5-KPS + 2D-106',
    runtime: 'ONNX Runtime (Server)'
  });
  const [telemetry, setTelemetry] = useState<{
    model: string;
    detector: string;
    confidence: number;
    yawOffset: number;
    mar: number;
    mouthOpen: number;
    latencyMs: number;
  } | null>(null);
  const [logs, setLogs] = useState<Array<{ id: string; time: string; message: string; type: 'info' | 'success' | 'warning' }>>([]);
  const [showLogs, setShowLogs] = useState<boolean>(true);
  const logsContainerRef = useRef<HTMLDivElement>(null);

  const addLog = (message: string, type: 'info' | 'success' | 'warning' = 'info') => {
    const time = new Date().toLocaleTimeString('ru-RU', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLogs((prev) => {
      if (prev.length > 0 && prev[prev.length - 1].message === message) {
        return prev;
      }
      return [...prev.slice(-25), { id: Math.random().toString(36).substring(2), time, message, type }];
    });
  };

  useEffect(() => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Состояния привязки через смартфон по QR
  const [openQrDialog, setOpenQrDialog] = useState<boolean>(false);
  const [qrToken, setQrToken] = useState<string>('');
  const [qrExpiresAt, setQrExpiresAt] = useState<number>(0);
  const [qrSecondsLeft, setQrSecondsLeft] = useState<number>(180);
  const [isQrLoading, setIsQrLoading] = useState<boolean>(false);
  const [qrReason, setQrReason] = useState<string>('');
  const qrPollIntervalRef = useRef<number | null>(null);

  const stopQrPolling = () => {
    if (qrPollIntervalRef.current) {
      window.clearInterval(qrPollIntervalRef.current);
      qrPollIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopQrPolling();
    };
  }, []);

  // Таймер обратного отсчета QR
  useEffect(() => {
    if (openQrDialog && qrExpiresAt > 0) {
      const interval = window.setInterval(() => {
        const left = Math.max(0, Math.floor((qrExpiresAt - Date.now()) / 1000));
        setQrSecondsLeft(left);
        if (left <= 0) {
          stopQrPolling();
        }
      }, 1000);
      return () => window.clearInterval(interval);
    }
  }, [openQrDialog, qrExpiresAt]);

  // Открытие диалога QR-привязки со смартфона
  const openQrModal = async (customReason?: string) => {
    stopCamera();
    stopQrPolling();
    setOpenQrDialog(true);
    setIsQrLoading(true);
    setQrReason(customReason || 'Отсканируйте QR-код камерой смартфона для привязки лица к профилю:');

    try {
      const res = await axios.post(`${API_CONFIG.BIOMETRICS_V2_URL}/qr/create`, { mode: 'register' });

      if (res.data?.success && res.data?.qrToken) {
        const tokenVal = res.data.qrToken;
        setQrToken(tokenVal);
        setQrExpiresAt(res.data.expiresAt);
        setQrSecondsLeft(Math.floor((res.data.expiresAt - Date.now()) / 1000));
        setIsQrLoading(false);

        // Polling статуса QR-сессии каждые 1.5 сек
        qrPollIntervalRef.current = window.setInterval(async () => {
          try {
            const statusRes = await axios.get(`${API_CONFIG.BIOMETRICS_V2_URL}/qr/status/${tokenVal}`);
            if (statusRes.data?.success && statusRes.data?.status === 'completed') {
              stopQrPolling();
              setOpenQrDialog(false);
              setHasBiometrics(true);
              setUpdatedAt(new Date().toISOString());
              notify.success('Биометрия лица успешно привязана через смартфон!');
              if (onStatusChange) onStatusChange(true);
              fetchStatus();
            } else if (statusRes.data?.status === 'expired' || statusRes.data?.status === 'invalid') {
              stopQrPolling();
            }
          } catch (pollErr) {
            console.warn('[BiometricsSection] Ошибка опроса QR:', pollErr);
          }
        }, 1500);
      } else {
        throw new Error('Сервер не вернул токен QR-сессии');
      }
    } catch (qrErr: any) {
      console.error('[BiometricsSection] Ошибка создания QR-сессии:', qrErr);
      setIsQrLoading(false);
      notify.error('Не удалось сформировать QR-код. Попробуйте еще раз.');
    }
  };

  // Загрузка статуса привязки биометрии с бэкенда
  const fetchStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_CONFIG.BIOMETRICS_V2_URL}/status`);

      if (res.data?.success) {
        setHasBiometrics(Boolean(res.data.hasBiometrics));
        setUpdatedAt(res.data.updatedAt || res.data.registeredAt || null);
      }
    } catch (err) {
      console.warn('[BiometricsSection] Не удалось загрузить статус биометрии:', err);
    } finally {
      setLoading(false);
    }
  };

  // Загрузка списка доступных камер с фильтрацией принтеров/сканеров
  const loadDevices = async () => {
    try {
      if (!navigator?.mediaDevices?.enumerateDevices) return;
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((d) => d.kind === 'videoinput');
      const isExcluded = (label: string) => /laserjet|scanner|printer|obs virtual|m426|fax/i.test(label);
      const isPreferred = (label: string) => /integrated|webcam|camera|камера|front|usb/i.test(label);

      const realCameras = videoInputs.filter((d) => !isExcluded(d.label));
      const sorted = (realCameras.length > 0 ? realCameras : videoInputs).sort((a, b) => {
        const aPref = isPreferred(a.label) ? 1 : 0;
        const bPref = isPreferred(b.label) ? 1 : 0;
        return bPref - aPref;
      });

      setAvailableDevices(sorted);
      if (sorted.length > 0) {
        setSelectedDeviceId((prev) => prev || sorted[0].deviceId);
      }
    } catch (e) {
      console.warn('[BiometricsSection] Ошибка перечисления устройств:', e);
    }
  };

  // Статус - свой, пользователя определяет сервер по сессии: загружается один раз при открытии секции
  useEffect(() => {
    fetchStatus();
    loadDevices();
  }, []);


  // Остановка камеры
  const stopCamera = () => {
    isScanningRef.current = false;
    if (scanLoopRef.current) {
      window.clearTimeout(scanLoopRef.current);
      cancelAnimationFrame(scanLoopRef.current);
      scanLoopRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    setIsScanning(false);
  };

  // Безопасное подключение к камере с интеллектуальной фильтрацией сканеров и приоритизацией веб-камер
  const requestCameraStream = async (deviceIdToUse?: string): Promise<MediaStream> => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      throw new Error(
        'Доступ к веб-камере не поддерживается браузером или страница открыта не через HTTPS. Убедитесь, что в адресной строке используется https:// или localhost.'
      );
    }

    // Обязательно освобождаем предыдущие треки перед новым запросом
    if (mediaStreamRef.current) {
      try {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      } catch (e) {
        console.warn('Ошибка остановки старого потока:', e);
      }
      mediaStreamRef.current = null;
    }

    // 1. Получаем список всех доступных видеоустройств
    let videoDevices: MediaDeviceInfo[] = [];
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      videoDevices = devices.filter((d) => d.kind === 'videoinput');
    } catch (e) {
      console.warn('[BiometricsSection] enumerateDevices error:', e);
    }

    const isExcluded = (label: string) => /laserjet|scanner|printer|obs virtual|m426|fax/i.test(label);
    const isPreferred = (label: string) => /integrated|webcam|camera|камера|front|usb/i.test(label);

    const filtered = videoDevices.filter((d) => !isExcluded(d.label));
    const candidates = (filtered.length > 0 ? filtered : videoDevices).sort((a, b) => {
      const aPref = isPreferred(a.label) ? 1 : 0;
      const bPref = isPreferred(b.label) ? 1 : 0;
      return bPref - aPref;
    });

    const targetDevId = deviceIdToUse || selectedDeviceId;
    if (targetDevId) {
      const idx = candidates.findIndex((d) => d.deviceId === targetDevId);
      if (idx > 0) {
        const [chosen] = candidates.splice(idx, 1);
        candidates.unshift(chosen);
      }
    }

    // 2. Поочередно пробуем запустить найденные реальные камеры
    let lastError: any = null;
    for (const dev of candidates) {
      try {
        const constraints: MediaStreamConstraints = {
          video: dev.deviceId
            ? {
              deviceId: { ideal: dev.deviceId },
              width: { ideal: 640 },
              height: { ideal: 480 }
            }
            : { width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        console.log(`[BiometricsSection] Успешно подключена камера: "${dev.label || dev.deviceId || 'default'}"`);
        if (dev.deviceId && dev.deviceId !== selectedDeviceId) {
          setSelectedDeviceId(dev.deviceId);
        }
        loadDevices();
        return stream;
      } catch (err: any) {
        console.warn(`[BiometricsSection] Ошибка подключения камеры "${dev.label || dev.deviceId}":`, err);
        lastError = err;
      }
    }

    // 3. Запасной прямой запуск через video: true
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false
      });
      loadDevices();
      return stream;
    } catch (directErr: any) {
      lastError = directErr;
    }

    console.error('[BiometricsSection] Не удалось запустить ни одну камеру:', lastError);
    const msg = String(lastError?.message || lastError?.name || '');

    if (msg.includes('Timeout') || lastError?.name === 'AbortError' || lastError?.name === 'NotReadableError' || lastError?.name === 'TrackStartError') {
      throw new Error(
        'Камера занята другим процессом. Если у вас открыто всплывающее окно предпросмотра камеры в строке адреса браузера или запущен Zoom/Teams/Skype/Камера Windows - закройте их и нажмите «Повторить».'
      );
    }
    if (lastError?.name === 'NotAllowedError' || lastError?.name === 'PermissionDeniedError') {
      throw new Error('Доступ к камере заблокирован в браузере. Разрешите использование камеры в настройках сайта (значок в адресной строке).');
    }
    if (lastError?.name === 'NotFoundError' || lastError?.name === 'DevicesNotFoundError') {
      throw new Error('Веб-камера не найдена на вашем устройстве. Убедитесь, что камера подключена.');
    }
    if (lastError?.name === 'OverconstrainedError') {
      throw new Error('Параметры веб-камеры не поддерживаются вашей системой.');
    }
    throw new Error(`Не удалось запустить камеру: ${lastError?.message || lastError?.name || 'Ошибка видеопотока'}`);
  };

  // Захват кадра в base64 JPEG
  const captureFrameBase64 = (): string | null => {
    if (!videoRef.current || videoRef.current.paused || videoRef.current.ended) return null;
    const video = videoRef.current;
    if (video.videoWidth === 0 || video.videoHeight === 0) return null;

    if (!hiddenCanvasRef.current) {
      hiddenCanvasRef.current = document.createElement('canvas');
    }
    const canvas = hiddenCanvasRef.current;
    canvas.width = 480;
    canvas.height = 360;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, 480, 360);
    return canvas.toDataURL('image/jpeg', 0.85);
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'turn_left': return 'поверните голову влево';
      case 'turn_right': return 'поверните голову вправо';
      case 'smile': return 'улыбнитесь';
      case 'tilt_head': return 'наклоните голову к плечу';
      case 'nod': return 'кивните головой вниз';
      case 'move_closer': return 'приблизьтесь чуть ближе к камере';
      default: return action;
    }
  };

  // Запуск камеры и процесса регистрации через сервер
  const startScanning = async (deviceIdOverride?: string | React.MouseEvent) => {
    setScanError(null);
    setScanProgress(5);
    setScanStep('center');
    isScanningRef.current = true;
    setScanMessage('Инициализация сессии FaceID...');
    setIsScanning(true);
    setLogs([]);
    addLog('Запуск сессии привязки FaceID...', 'info');

    // 1. Создаем серверную сессию Liveness
    try {
      const chalRes = await axios.post(`${API_CONFIG.BIOMETRICS_V2_URL}/server/liveness/start`);
      if (chalRes.data?.success && chalRes.data?.livenessSessionId) {
        livenessSessionIdRef.current = chalRes.data.livenessSessionId;
        actionsSequenceRef.current = chalRes.data.sequence || [];
        if (chalRes.data.modelInfo) {
          setModelInfo(chalRes.data.modelInfo);
          addLog(`Модели активированы: ${chalRes.data.modelInfo.recognizer}`, 'success');
        } else {
          addLog('Серверная сессия проверки создана', 'success');
        }
      } else {
        throw new Error('Сервер не вернул сессию проверки');
      }
    } catch (sErr: any) {
      console.error('[BiometricsSection] Ошибка старта серверной сессии:', sErr);
      setScanError('Не удалось подключиться к серверу распознавания лиц.');
      setIsScanning(false);
      addLog('Не удалось подключиться к серверу распознавания', 'warning');
      return;
    }

    try {
      setScanMessage('Подключение к веб-камере...');
      addLog('Поиск веб-камеры и запуск видеопотока...', 'info');
      const targetDev = typeof deviceIdOverride === 'string' ? deviceIdOverride : undefined;
      const stream = await requestCameraStream(targetDev);
      addLog(`Камера запущена: ${stream.getVideoTracks()[0]?.label || 'Веб-камера'}`, 'success');

      mediaStreamRef.current = stream;
      if (videoRef.current) {
        const video = videoRef.current;
        video.srcObject = stream;

        let started = false;
        const onReady = () => {
          if (started) return;
          started = true;
          setScanMessage('Посмотрите прямо в камеру');
          setScanProgress(20);
          runDetectionLoop();
        };

        video.onloadedmetadata = () => {
          video.play().then(onReady).catch((pErr) => {
            console.warn('[BiometricsSection] video.play() err:', pErr);
            onReady();
          });
        };

        video.play().then(onReady).catch(() => { });
      }
    } catch (camErr: any) {
      console.warn('[BiometricsSection] Ошибка доступа к камере, переключение на QR:', camErr);
      stopCamera();
      openQrModal('Веб-камера на ПК недоступна. Отсканируйте QR-код камерой смартфона для привязки лица:');
    }
  };

  // Единая точка запуска привязки биометрии (1 кнопка без лишнего выбора для пользователя)
  const handleInitiateBinding = async () => {
    // 1. Быстрая проверка поддержки API медиаустройств
    if (!navigator?.mediaDevices?.getUserMedia) {
      openQrModal('Веб-камера на ПК недоступна. Отсканируйте QR-код смартфоном для привязки лица к профилю:');
      return;
    }

    // 2. Проверка наличия подключенных физических видеоустройств
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((d) => d.kind === 'videoinput');
      const isExcluded = (label: string) => /laserjet|scanner|printer|obs virtual|m426|fax/i.test(label);
      const realCameras = videoInputs.filter((d) => !isExcluded(d.label));

      // Если физических камер нет - сразу открываем QR без сбоев и лишних кликов
      if (videoInputs.length === 0 || (realCameras.length === 0 && videoInputs.length > 0 && videoInputs.every((d) => isExcluded(d.label)))) {
        openQrModal('На вашем компьютере не обнаружена веб-камера. Отсканируйте QR-код смартфоном для быстрой привязки FaceID:');
        return;
      }
    } catch (enumErr) {
      console.warn('[BiometricsSection] Ошибка опроса устройств:', enumErr);
    }

    // 3. Если камера потенциально доступна - запускаем процесс сканирования на ПК
    startScanning();
  };

  // Цикл отправки кадров на сервер для детекции и liveness
  const runDetectionLoop = async () => {
    if (!isScanningRef.current || !videoRef.current) return;

    if (!isProcessingFrameRef.current && livenessSessionIdRef.current) {
      const frame = captureFrameBase64();
      if (frame) {
        isProcessingFrameRef.current = true;
        const tStart = performance.now();
        try {
          const res = await axios.post(`${API_CONFIG.BIOMETRICS_V2_URL}/server/liveness/process-frame`, {
            livenessSessionId: livenessSessionIdRef.current,
            frame
          });
          const latencyMs = Math.round(performance.now() - tStart);

          if (res.data?.success) {
            const data = res.data;
            if (data.telemetry) {
              setTelemetry({
                ...data.telemetry,
                latencyMs
              });
            }

            if (data.phase === 'completed') {
              // Сервер подтвердил liveness и извлек ArcFace дескриптор
              isScanningRef.current = false;
              setScanStep('saving');
              setScanProgress(100);
              setScanMessage('Сохранение биометрического слепка (ArcFace)...');
              addLog('Liveness подтвержден! Извлечен 512-dim вектор ArcFace', 'success');

              await finalizeRegistration();
              return;
            }

            if (data.phase === 'calibrating') {
              setScanStep('center');
              if (data.faceDetected) {
                const samples = data.calibrationProgress || 0;
                setScanMessage(`Калибровка ракурса лица (${samples}/5)...`);
                setScanProgress(20 + samples * 6);
                if (samples > 0) {
                  addLog(`Калибровка лица: ${samples}/5 (ракурс: ${data.telemetry?.yawOffset ?? '0.00'})`, 'info');
                }
              } else {
                setScanMessage('Поместите лицо в центр рамки');
              }
            }

            if (data.phase === 'challenge') {
              const currentAction = data.currentAction;
              if (currentAction === 'turn_left' || currentAction === 'turn_right') {
                setScanStep(currentAction === 'turn_left' ? 'left' : 'right');
              }
              const completedCount = Array.isArray(data.completedActions) ? data.completedActions.length : 0;
              const total = Math.max(actionsSequenceRef.current.length, 1);
              setScanProgress(50 + (completedCount / total) * 40);

              if (currentAction) {
                setScanMessage(`Отлично! Теперь ${getActionLabel(currentAction)}`);
              }
              if (data.actionCompleted) {
                addLog(`Действие "${getActionLabel(data.actionCompleted)}" подтверждено`, 'success');
              }
            }
          } else if (res.data?.code === 'ANTI_SWAP_TRIGGERED') {
            setScanMessage('Держите лицо ровно по центру');
            setScanProgress(20);
            addLog('Anti-Swap: зафиксирован скачок координат лица', 'warning');
          }
        } catch (err: any) {
          console.warn('[BiometricsSection] Ошибка кадра на сервере:', err.message);
        } finally {
          isProcessingFrameRef.current = false;
        }
      }
    }

    if (isScanningRef.current) {
      scanLoopRef.current = window.setTimeout(runDetectionLoop, 120) as any;
    }
  };

  // Регистрация серверного дескриптора в базе
  const finalizeRegistration = async () => {
    stopCamera();
    addLog('Сохранение дескриптора в профиль сотрудника...', 'info');

    try {
      setLoading(true);
      const res = await axios.post(`${API_CONFIG.BIOMETRICS_V2_URL}/server/register`, {
        livenessSessionId: livenessSessionIdRef.current
      });

      if (res.data?.success) {
        setHasBiometrics(true);
        setUpdatedAt(new Date().toISOString());
        addLog('Биометрический профиль успешно сохранен в MySQL', 'success');
        notify.success('Биометрия лица успешно привязана (ArcFace 512-dim)!');
        if (onStatusChange) onStatusChange(true);
      } else {
        addLog(`Ошибка сохранения: ${res.data?.message || 'сбой'}`, 'warning');
        notify.error(res.data?.message || 'Ошибка сохранения биометрии');
      }
    } catch (err: any) {
      console.error('[Biometrics] Ошибка отправки на бэкенд:', err);
      addLog('Сбой связи с сервером при сохранении дескриптора', 'warning');
      notify.error(err.response?.data?.message || 'Ошибка связи с сервером при регистрации лица');
    } finally {
      setLoading(false);
    }
  };

  // Сброс биометрии по кнопке
  const handleReset = async () => {
    try {
      setResetting(true);
      const res = await axios.post(`${API_CONFIG.BIOMETRICS_V2_URL}/reset`);
      if (res.data?.success) {
        setHasBiometrics(false);
        setUpdatedAt(null);
        notify.warning('Биометрический слепок удален. Вход по лицу отключен до новой привязки.');
        if (onStatusChange) onStatusChange(false);
      } else {
        notify.error(res.data?.message || 'Ошибка при сбросе биометрии');
      }
    } catch (err: any) {
      notify.error(err.response?.data?.message || 'Ошибка при сбросе биометрии');
    } finally {
      setResetting(false);
      setOpenResetDialog(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        borderRadius: '20px',
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.03)',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        scrollMarginTop: '24px'
      }}
    >
      <SectionHeaderDivider
        id="header-biometrics"
        icon={<ScanFace className="size-5 text-emerald-600" />}
        title="Биометрия (FaceID)"
        subtitle="Аутентификация по лицу для быстрого и безопасного входа"
        badge={hasBiometrics ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircle sx={{ fontSize: 14, color: ZERDE_COLORS.emerald }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#047857', lineHeight: 1 }}>
              Биометрия активна
            </Typography>
          </Box>
        ) : undefined}
      />

      {/* Режим активного сканирования через камеру */}
      {isScanning ? (
        <Box
          sx={{
            p: 3,
            borderRadius: '16px',
            bgcolor: '#0a0a0a',
            border: '1px solid #27272a',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5
          }}
        >
          {/* Индикатор активной серверной нейросети */}
          {modelInfo && (
            <Chip
              icon={<Memory sx={{ fontSize: '15px !important', color: '#10b981' }} />}
              label={`${modelInfo.recognizer} • ${modelInfo.detector}`}
              size="small"
              sx={{
                bgcolor: 'rgba(16, 185, 129, 0.12)',
                color: '#34d399',
                fontWeight: 700,
                fontSize: '0.78rem',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                letterSpacing: 0.2
              }}
            />
          )}

          <Typography variant="h6" fontWeight={700} sx={{ color: '#fff' }}>
            Калибровка и сканирование лица
          </Typography>

          {/* Окно визира камеры */}
          <Box
            sx={{
              position: 'relative',
              width: { xs: 280, sm: 340 },
              height: { xs: 210, sm: 255 },
              borderRadius: '20px',
              border: '2px dashed #10b981',
              bgcolor: '#000',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 25px rgba(16, 185, 129, 0.2)'
            }}
          >
            <video
              ref={videoRef}
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scaleX(-1)' // Зеркальное отображение для естественности
              }}
            />

            {/* Сканирующая анимированная линия */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                height: '2px',
                bgcolor: '#34d399',
                boxShadow: '0 0 10px #34d399',
                animation: 'scanAnim 2.2s ease-in-out infinite',
                '@keyframes scanAnim': {
                  '0%': { top: '5%' },
                  '50%': { top: '90%' },
                  '100%': { top: '5%' }
                }
              }}
            />
          </Box>

          {scanError ? (
            <Box sx={{ width: '100%', maxWidth: 420, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert
                severity="error"
                sx={{
                  borderRadius: '12px',
                  bgcolor: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  color: '#fca5a5',
                  textAlign: 'left',
                  fontSize: '0.85rem'
                }}
              >
                {scanError}
              </Alert>
              {availableDevices.length > 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, alignItems: 'center', width: '100%' }}>
                  <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                    Выберите другой источник видео:
                  </Typography>
                  <select
                    value={selectedDeviceId}
                    onChange={(e) => {
                      const newId = e.target.value;
                      setSelectedDeviceId(newId);
                      stopCamera();
                      setTimeout(() => startScanning(newId), 200);
                    }}
                    style={{
                      width: '100%',
                      maxWidth: '360px',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      backgroundColor: '#18181b',
                      color: '#e4e4e7',
                      border: '1px solid #3f3f46',
                      fontSize: '0.85rem',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    {availableDevices.map((d, i) => (
                      <option key={d.deviceId || i} value={d.deviceId}>
                        {d.label || `Камера ${i + 1}`}
                      </option>
                    ))}
                  </select>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={stopCamera}
                  startIcon={<Close />}
                  sx={{
                    color: '#9ca3af',
                    borderColor: '#374151',
                    borderRadius: '10px',
                    textTransform: 'none',
                    '&:hover': { borderColor: '#ef4444', color: '#ef4444' }
                  }}
                >
                  Закрыть
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => openQrModal('Веб-камера недоступна. Вы можете пройти привязку со смартфона:')}
                  startIcon={<PhoneIphone />}
                  sx={{
                    color: '#34d399',
                    borderColor: '#059669',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { borderColor: '#10b981', bgcolor: 'rgba(16, 185, 129, 0.1)' }
                  }}
                >
                  Привязать через смартфон (QR)
                </Button>
                <Button
                  variant="contained"
                  onClick={() => startScanning(selectedDeviceId)}
                  startIcon={<Refresh />}
                  sx={{
                    borderRadius: '10px',
                    bgcolor: ZERDE_COLORS.primary,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#1058c4' }
                  }}
                >
                  Повторить
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              {/* Индикатор прогресса и текущее задание */}
              <Box sx={{ width: '100%', maxWidth: 440, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#34d399', fontWeight: 600, mb: 1, fontSize: '0.95rem' }}>
                  {scanMessage}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={scanProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiLinearProgress-bar': { bgcolor: '#10b981' }
                  }}
                />
              </Box>

              {/* Панель телеметрии и окно отладочных логов */}
              <Box sx={{ width: '100%', maxWidth: 440, textAlign: 'left', mt: 1 }}>
                <Box
                  onClick={() => setShowLogs((prev) => !prev)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    py: 0.7,
                    px: 1.4,
                    borderRadius: '10px',
                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <TerminalOutlined sx={{ fontSize: 16, color: '#38bdf8' }} />
                    <Typography variant="caption" sx={{ color: '#cbd5e1', fontWeight: 700, fontSize: '0.74rem' }}>
                      Телеметрия нейросети и логи
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {telemetry && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: telemetry.confidence > 70 ? '#34d399' : '#fbbf24',
                          fontFamily: 'monospace',
                          fontSize: '0.72rem',
                          fontWeight: 600
                        }}
                      >
                        {telemetry.confidence > 0 ? `SCRFD: ${telemetry.confidence}%` : 'Поиск...'}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', userSelect: 'none' }}>
                      {showLogs ? 'Скрыть ▲' : 'Развернуть ▼'}
                    </Typography>
                  </Box>
                </Box>

                <Collapse in={showLogs}>
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.2,
                      borderRadius: '12px',
                      bgcolor: '#05070a',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.5)'
                    }}
                  >
                    {/* Сетка живых метрик телеметрии */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.8, mb: 1 }}>
                      <Box sx={{ p: 0.6, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.03)', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.62rem', display: 'block' }}>
                          SCRFD
                        </Typography>
                        <Typography variant="caption" sx={{ color: telemetry && telemetry.confidence > 60 ? '#10b981' : '#94a3b8', fontWeight: 800, fontFamily: 'monospace', fontSize: '0.74rem' }}>
                          {telemetry && telemetry.confidence > 0 ? `${telemetry.confidence}%` : '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 0.6, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.03)', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.62rem', display: 'block' }}>
                          Ракурс (Yaw)
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#38bdf8', fontWeight: 800, fontFamily: 'monospace', fontSize: '0.74rem' }}>
                          {telemetry ? (telemetry.yawOffset > 0 ? `+${telemetry.yawOffset}` : telemetry.yawOffset) : '0.00'}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 0.6, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.03)', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.62rem', display: 'block' }}>
                          Мимика (MAR)
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 800, fontFamily: 'monospace', fontSize: '0.74rem' }}>
                          {telemetry ? telemetry.mar : '0.00'}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 0.6, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.03)', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.62rem', display: 'block' }}>
                          Отклик (RTT)
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#fbbf24', fontWeight: 800, fontFamily: 'monospace', fontSize: '0.74rem' }}>
                          {telemetry?.latencyMs ? `${telemetry.latencyMs} мс` : '-'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Мини-терминал логов */}
                    <Box
                      ref={logsContainerRef}
                      sx={{
                        maxHeight: 110,
                        overflowY: 'auto',
                        pr: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.3,
                        fontSize: '0.7rem',
                        fontFamily: 'Consolas, "Roboto Mono", Menlo, monospace',
                        '&::-webkit-scrollbar': { width: 4 },
                        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255, 255, 255, 0.15)', borderRadius: 2 }
                      }}
                    >
                      {logs.length === 0 ? (
                        <Typography variant="caption" sx={{ color: '#475569', fontStyle: 'italic', fontSize: '0.68rem' }}>
                          Ожидание первого кадра...
                        </Typography>
                      ) : (
                        logs.map((item) => (
                          <Box key={item.id} sx={{ display: 'flex', gap: 0.8, lineHeight: 1.3 }}>
                            <Typography component="span" sx={{ color: '#475569', fontSize: '0.65rem', flexShrink: 0 }}>
                              {item.time}
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                color: item.type === 'success' ? '#34d399' : item.type === 'warning' ? '#f87171' : '#cbd5e1',
                                fontSize: '0.68rem',
                                wordBreak: 'break-word'
                              }}
                            >
                              {item.message}
                            </Typography>
                          </Box>
                        ))
                      )}
                    </Box>
                  </Box>
                </Collapse>
              </Box>

              <Button
                variant="outlined"
                onClick={stopCamera}
                startIcon={<Close />}
                sx={{
                  color: '#9ca3af',
                  borderColor: '#374151',
                  borderRadius: '10px',
                  '&:hover': { borderColor: '#ef4444', color: '#ef4444' }
                }}
              >
                Отмена
              </Button>
            </>
          )}
        </Box>
      ) : (
        /* Режим отображения текущего статуса */
        <Box
          sx={{
            p: 2.5,
            borderRadius: '16px',
            bgcolor: '#f8fafc',
            border: '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  bgcolor: hasBiometrics ? 'rgba(16, 185, 129, 0.12)' : '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: hasBiometrics ? '#059669' : '#64748b'
                }}
              >
                <ScanFace className="size-6" />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>
                  {hasBiometrics ? 'Биометрический слепок привязан' : 'Биометрия не привязана'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem' }}>
                  {!hasBiometrics
                    ? 'Привяжите лицо для входа в портал без ввода пароля. При смене ФИО привязка сбрасывается'
                    : updatedAt
                      ? `Обновлено: ${new Date(updatedAt).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' })}`
                      : 'Вход по лицу включен'}
                </Typography>
              </Box>
            </Box>

            {/* Кнопки управления (единая кнопка для привязки) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {hasBiometrics ? (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<Refresh />}
                    onClick={handleInitiateBinding}
                    disabled={loading}
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                  >
                    Перепривязать
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutline />}
                    onClick={() => setOpenResetDialog(true)}
                    disabled={loading || resetting}
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                  >
                    Сбросить
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<VideocamOutlined />}
                  onClick={handleInitiateBinding}
                  disabled={loading}
                  sx={{
                    borderRadius: '12px',
                    bgcolor: ZERDE_COLORS.primary,
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 2.5,
                    py: 1,
                    '&:hover': { bgcolor: '#1058c4' }
                  }}
                >
                  Привязать FaceID
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Диалог подтверждения сброса */}
      <Dialog
        open={openResetDialog}
        onClose={() => setOpenResetDialog(false)}
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        <DialogTitle fontWeight={700}>Сбросить биометрию лица?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Ваш текущий слепок FaceID будет полностью удален из базы данных. Вы не сможете войти по лицу, пока не
            привяжете биометрию заново.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenResetDialog(false)} sx={{ textTransform: 'none' }}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReset}
            disabled={resetting}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
          >
            {resetting ? <CircularProgress size={20} color="inherit" /> : 'Да, сбросить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Модальное окно привязки FaceID через QR-код */}
      <Dialog
        open={openQrDialog}
        onClose={() => {
          stopQrPolling();
          setOpenQrDialog(false);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            p: 3,
            bgcolor: '#18181b',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'center'
          }
        }}
      >
        <DialogTitle sx={{ p: 0, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIphone sx={{ color: '#10b981' }} />
            <Typography variant="h6" fontWeight={700} sx={{ color: '#fff' }}>
              Привязка FaceID
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              stopQrPolling();
              setOpenQrDialog(false);
            }}
            sx={{ color: '#9ca3af', '&:hover': { color: '#ef4444' } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, my: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
            {qrReason}
          </Typography>

          {isQrLoading ? (
            <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={42} sx={{ color: '#10b981' }} />
              <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                Формирование QR-кода...
              </Typography>
            </Box>
          ) : qrToken ? (
            <>
              {/* Контейнер QR-кода */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#ffffff',
                  borderRadius: '16px',
                  boxShadow: '0 0 25px rgba(16, 185, 129, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}
              >
                <QRCodeSVG
                  value={`${window.location.origin}/mobile-faceid?token=${qrToken}&mode=register`}
                  size={200}
                  level="M"
                  includeMargin={false}
                />
              </Box>

              {/* Таймер действия */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Chip
                  icon={<TimerOutlined sx={{ fontSize: '15px !important', color: qrSecondsLeft <= 30 ? '#ef4444' : '#10b981' }} />}
                  label={`Код действителен: ${Math.floor(qrSecondsLeft / 60)}:${String(qrSecondsLeft % 60).padStart(2, '0')}`}
                  size="small"
                  sx={{
                    bgcolor: qrSecondsLeft <= 30 ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.12)',
                    color: qrSecondsLeft <= 30 ? '#f87171' : '#34d399',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    border: qrSecondsLeft <= 30 ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(16, 185, 129, 0.3)'
                  }}
                />
              </Box>

              <Typography variant="caption" sx={{ color: '#64748b', maxWidth: 300, lineHeight: 1.4 }}>
                1. Откройте камеру на телефоне и наведите на код
                <br />
                2. Пройдите проверку лица (2 жеста)
                <br />
                3. Профиль на этом экране обновится сам
              </Typography>
            </>
          ) : (
            <Alert severity="error" sx={{ my: 2, width: '100%', borderRadius: '12px' }}>
              Не удалось загрузить сессию QR-кода
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 0, mt: 2, justifyContent: 'center', gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() => {
              stopQrPolling();
              setOpenQrDialog(false);
            }}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              color: '#9ca3af',
              borderColor: '#3f3f46',
              '&:hover': { borderColor: '#ef4444', color: '#ef4444' }
            }}
          >
            Закрыть
          </Button>
          {qrSecondsLeft <= 0 && (
            <Button
              variant="contained"
              onClick={() => openQrModal(qrReason)}
              startIcon={<Refresh />}
              sx={{
                borderRadius: '10px',
                bgcolor: '#10b981',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#059669' }
              }}
            >
              Обновить QR-код
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BiometricsSection;
